import type {
  CadastrarCurvaBody,
  CategoriaCurva,
  CurvaItem,
  EditarCurvaBody,
} from "@/modules/financeiro-configuracoes";
import { montarPrevia } from "../curvas.utils";
import type { PreviaFase } from "../types-curvas";
import {
  CATEGORIA_OBRIGATORIA,
  MESES_INVALIDO,
  MESES_MAX,
  MESES_OBRIGATORIO_ANTES,
  MESES_OBRIGATORIO_APOS,
  NOME_MAX,
  NOME_MUITO_LONGO,
  NOME_OBRIGATORIO,
  PERCENTUAL_INVALIDO,
  SOMA_ALVO,
  SOMA_TOLERANCIA,
} from "./curva-dialog.constants";

export type CurvaForm = {
  nome: string;
  categoria: CategoriaCurva | null;
  percentualAntes: number | null;
  mesesAntes: number | null;
  percentualDurante: number | null;
  percentualApos: number | null;
  mesesApos: number | null;
};

export const CURVA_VAZIA: CurvaForm = {
  nome: "",
  categoria: null,
  percentualAntes: null,
  mesesAntes: null,
  percentualDurante: null,
  percentualApos: null,
  mesesApos: null,
};

export const formDaCurva = (curva: CurvaItem): CurvaForm => ({
  nome: curva.nome,
  categoria: curva.categoria,
  percentualAntes: curva.antes.percentual,
  mesesAntes: curva.antes.meses,
  percentualDurante: curva.durante.percentual,
  percentualApos: curva.depois.percentual,
  mesesApos: curva.depois.meses,
});

const centesimos = (valor: number | null) => Math.round((valor ?? 0) * 100);

export function erroNome(nome: string): string | null {
  const limpo = nome.trim();
  if (limpo === "") return NOME_OBRIGATORIO;
  return limpo.length > NOME_MAX ? NOME_MUITO_LONGO : null;
}

export const erroCategoria = (categoria: CategoriaCurva | null): string | null =>
  categoria ? null : CATEGORIA_OBRIGATORIA;

export function erroPercentual(valor: number | null): string | null {
  if (valor === null) return null;
  const valido = valor >= 0 && valor <= 100 && centesimos(valor) / 100 === valor;
  return valido ? null : PERCENTUAL_INVALIDO;
}

export function erroMeses(valor: number | null): string | null {
  if (valor === null) return null;
  return Number.isInteger(valor) && valor >= 0 && valor <= MESES_MAX ? null : MESES_INVALIDO;
}

export function erroMesesObrigatorio(
  percentual: number | null,
  meses: number | null,
  fase: "antes" | "apos",
): string | null {
  if ((percentual ?? 0) <= 0 || (meses ?? 0) > 0) return null;
  return fase === "antes" ? MESES_OBRIGATORIO_ANTES : MESES_OBRIGATORIO_APOS;
}

export const somaPercentuais = (form: CurvaForm): number =>
  (centesimos(form.percentualAntes) +
    centesimos(form.percentualDurante) +
    centesimos(form.percentualApos)) /
  100;

export const somaValida = (soma: number): boolean =>
  Math.abs(centesimos(soma) - SOMA_ALVO * 100) <= SOMA_TOLERANCIA * 100;

export const totalMeses = (form: CurvaForm): number =>
  (form.mesesAntes ?? 0) + (form.mesesApos ?? 0);

export function temCampoInvalido(form: CurvaForm): boolean {
  return (
    [form.percentualAntes, form.percentualDurante, form.percentualApos].some(
      (valor) => erroPercentual(valor) !== null,
    ) ||
    [form.mesesAntes, form.mesesApos].some((valor) => erroMeses(valor) !== null) ||
    erroMesesObrigatorio(form.percentualAntes, form.mesesAntes, "antes") !== null ||
    erroMesesObrigatorio(form.percentualApos, form.mesesApos, "apos") !== null
  );
}

export const podeSalvar = (form: CurvaForm): boolean =>
  !temCampoInvalido(form) && somaValida(somaPercentuais(form));

export const previaDoForm = (form: CurvaForm): PreviaFase[] =>
  montarPrevia({
    antes: { percentual: form.percentualAntes ?? 0, meses: form.mesesAntes ?? 0 },
    durante: { percentual: form.percentualDurante ?? 0 },
    depois: { percentual: form.percentualApos ?? 0, meses: form.mesesApos ?? 0 },
  });

function corpoComum(form: CurvaForm) {
  return {
    nome: form.nome.trim(),
    percentualAntes: form.percentualAntes,
    mesesAntes: form.mesesAntes,
    percentualDurante: form.percentualDurante,
    percentualDepois: form.percentualApos,
    mesesDepois: form.mesesApos,
  };
}

export function montarCorpoNova(form: CurvaForm, categoria: CategoriaCurva): CadastrarCurvaBody {
  return { ...corpoComum(form), categoria };
}

export const montarCorpoEdicao = (form: CurvaForm): EditarCurvaBody => corpoComum(form);
